import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { farmerApi } from '../../../services/api';
import FarmerDetail from './FarmerDetail';
import LoadingIndicator from '../../common/LoadingIndicator';
import { useData } from '../../../context/DataContext';

/**
 * Unified Farmer component that handles both local and API data
 */
const Farmer = ({ useApi = true, localFarmers = [], onEdit, onDelete }) => {
  const navigate = useNavigate();
  const { farmers: contextFarmers } = useData();
  const [farmers, setFarmers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedFarmerId, setSelectedFarmerId] = useState(null);

  // Get unique locations for filter dropdown
  const locations = [...new Set(farmers.map(farmer =>
    farmer.village || farmer.district || farmer.location || 'Unknown'
  ).filter(Boolean))];

  // Filter farmers based on search term and location filter
  const filteredFarmers = farmers.filter(farmer => {
    const name = farmer.farmer_name || farmer.name || '';
    const mobile = farmer.mobile || farmer.email || '';
    const location = farmer.village || farmer.district || farmer.location || '';

    const matchesSearch =
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mobile.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesLocation = locationFilter === '' || location === locationFilter;

    return matchesSearch && matchesLocation;
  });

  // Load farmers data
  useEffect(() => {
    if (useApi) {
      fetchFarmers();
    } else {
      setFarmers(localFarmers);
      setLoading(false);
    }
  }, [useApi, localFarmers]);

  // Fetch farmers from API
  const fetchFarmers = async () => {
    setLoading(true);
    try {
      const data = await farmerApi.getAllFarmers();
      setFarmers(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching farmers:', err);
      setError('Failed to load farmers. Please try again later.');
      setFarmers([]);
    } finally {
      setLoading(false);
    }
  };

  // Refresh data
  const refreshData = () => {
    if (useApi) {
      fetchFarmers();
    }
  };

  // Handle add button click
  const handleAddClick = () => {
    navigate('/admin/farmer-form');
  };

  // Handle edit farmer (for local data)
  const handleEditFarmer = (farmer) => {
    if (onEdit) {
      onEdit(farmer);
    }
  };

  // Handle delete farmer (for local data)
  const handleDeleteFarmer = (id) => {
    if (onDelete) {
      onDelete(id);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.filterSection}>
        <div style={styles.filterHeader}>
          <div style={styles.filterTitle}>
            <span style={styles.filterIcon}>🔍</span>
            <span>Search & Filter</span>
          </div>
          <div style={styles.actionsContainer}>
            <div style={styles.resultsCount}>
              {filteredFarmers.length} {filteredFarmers.length === 1 ? 'farmer' : 'farmers'} found
            </div>
            {useApi && (
              <button
                onClick={handleAddClick}
                style={styles.addButtonSmall}
                title="Add New Farmer"
              >
                <span style={styles.buttonIcon}>➕</span>
                Add Farmer
              </button>
            )}
          </div>
        </div>

        <div style={styles.filters}>
          <div style={styles.searchBox}>
            <div style={styles.inputIcon}>🔍</div>
            <input
              type="text"
              placeholder="Search farmers by name, mobile or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={styles.clearButton}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          <div style={styles.filterBox}>
            <div style={styles.inputIcon}>🗺️</div>
            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="">All Locations</option>
              {locations.map(location => (
                <option key={location} value={location}>
                  {location}
                </option>
              ))}
            </select>
            {locationFilter && (
              <button
                onClick={() => setLocationFilter('')}
                style={styles.clearButton}
                title="Clear filter"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={styles.tableContainer}>
        <div style={styles.listHeader}>
          <div style={styles.headerName}>
            <span style={styles.headerIcon}>👨‍🌾</span>
            Farmer Name
          </div>
          <div style={styles.headerMobile}>
            <span style={styles.headerIcon}>📱</span>
            Mobile
          </div>
          <div style={styles.headerVillage}>
            <span style={styles.headerIcon}>🏡</span>
            Village
          </div>
          <div style={styles.headerDistrict}>
            <span style={styles.headerIcon}>🗺️</span>
            District
          </div>
          <div style={styles.headerAcreage}>
            <span style={styles.headerIcon}>📏</span>
            Acreage
          </div>
          <div style={styles.headerActions}>Actions</div>
        </div>

        <div style={styles.list}>
          {loading ? (
            <div style={styles.loadingContainer}>
              <LoadingIndicator />
              <p>Loading farmers...</p>
            </div>
          ) : error ? (
            <div style={styles.errorContainer}>
              <div style={styles.errorIcon}>⚠️</div>
              <p style={styles.errorText}>{error}</p>
              <button onClick={refreshData} style={styles.retryButton}>
                Retry
              </button>
            </div>
          ) : filteredFarmers.length > 0 ? (
            filteredFarmers.map(farmer => (
              <div key={farmer.id} style={styles.item}>
                <div style={styles.name}>
                  <span style={styles.nameText}>{farmer.farmer_name || farmer.name || 'N/A'}</span>
                </div>
                <div style={styles.mobile}>{farmer.mobile || farmer.email || 'N/A'}</div>
                <div style={styles.village}>{farmer.village || 'N/A'}</div>
                <div style={styles.district}>
                  {farmer.district ? (
                    <span style={styles.districtBadge}>{farmer.district}</span>
                  ) : (
                    'N/A'
                  )}
                </div>
                <div style={styles.acreage}>
                  {farmer.acreage ? (
                    <span style={styles.acreageValue}>{farmer.acreage} acres</span>
                  ) : (
                    'N/A'
                  )}
                </div>
                <div style={styles.actions}>
                  {useApi ? (
                    <>
                      <button
                        onClick={() => {
                          setSelectedFarmerId(farmer.id);
                          setShowDetailModal(true);
                        }}
                        style={styles.viewButton}
                        title="View Details"
                      >
                        <span style={styles.buttonIcon}>👁️</span>
                        <span style={styles.buttonText}>View</span>
                      </button>
                      <button
                        onClick={() => navigate(`/admin/farmer-form/${farmer.id}`)}
                        style={styles.editButton}
                        title="Edit Farmer"
                      >
                        <span style={styles.buttonIcon}>✏️</span>
                        <span style={styles.buttonText}>Edit</span>
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => handleEditFarmer(farmer)}
                        style={styles.editButton}
                        title="Edit"
                      >
                        <span style={styles.buttonIcon}>✏️</span>
                        <span style={styles.buttonText}>Edit</span>
                      </button>
                      <button
                        onClick={() => handleDeleteFarmer(farmer.id)}
                        style={styles.deleteButton}
                        title="Delete"
                      >
                        <span style={styles.buttonIcon}>🗑️</span>
                        <span style={styles.buttonText}>Delete</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div style={styles.noResults}>
              <div style={styles.noResultsIcon}>🔍</div>
              <div style={styles.noResultsText}>No farmers found matching your criteria.</div>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setLocationFilter('');
                }}
                style={styles.resetButton}
              >
                Reset Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedFarmerId && (
        <FarmerDetail
          farmerId={selectedFarmerId}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedFarmerId(null);
          }}
          onDelete={() => {
            setShowDetailModal(false);
            setSelectedFarmerId(null);
            refreshData();
          }}
        />
      )}
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: 'var(--background-light)',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
  },
  filterSection: {
    padding: '1.5rem',
    backgroundColor: 'white',
    borderBottom: '1px solid var(--border-color)',
    borderRadius: '8px 8px 0 0',
  },
  filterHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  filterTitle: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '1.1rem',
    fontWeight: '600',
    color: 'var(--text-dark)',
  },
  filterIcon: {
    marginRight: '0.5rem',
    fontSize: '1.2rem',
  },
  resultsCount: {
    fontSize: '0.9rem',
    color: 'var(--text-light)',
    backgroundColor: 'var(--background-light)',
    padding: '0.4rem 0.8rem',
    borderRadius: '20px',
  },
  actionsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  addButtonSmall: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.5rem 1rem',
    backgroundColor: 'var(--primary-color)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '0.9rem',
    transition: 'background-color 0.2s',
  },
  filters: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem',
  },
  searchBox: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  filterBox: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  inputIcon: {
    position: 'absolute',
    left: '1rem',
    color: 'var(--text-light)',
    zIndex: 1,
  },
  searchInput: {
    width: '100%',
    padding: '0.4rem 2.5rem',
    borderRadius: '6px',
    border: '1px solid var(--border-color)',
    fontSize: '1rem',
    backgroundColor: 'var(--background-color)',
    height: '42px',
  },
  clearButton: {
    position: 'absolute',
    right: '1rem',
    background: 'none',
    border: 'none',
    color: 'var(--text-light)',
    cursor: 'pointer',
    fontSize: '1rem',
  },
  filterSelect: {
    width: '100%',
    padding: '0.5rem 2.5rem',
    borderRadius: '6px',
    border: '1px solid var(--border-color)',
    fontSize: '1rem',
    backgroundColor: 'var(--background-color)',
    height: '42px',
    appearance: 'none',
  },
  tableContainer: {
    backgroundColor: 'white',
    borderRadius: '0 0 8px 8px',
  },
  listHeader: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr',
    gap: '1rem',
    padding: '1rem',
    backgroundColor: 'var(--primary-light)',
    color: 'white',
    fontWeight: 'bold',
    borderBottom: '2px solid var(--primary-color)',
  },
  headerIcon: {
    marginRight: '0.5rem',
    fontSize: '0.9rem',
  },
  headerName: {
    display: 'flex',
    alignItems: 'center',
  },
  headerMobile: {
    display: 'flex',
    alignItems: 'center',
  },
  headerVillage: {
    display: 'flex',
    alignItems: 'center',
  },
  headerDistrict: {
    display: 'flex',
    alignItems: 'center',
  },
  headerAcreage: {
    display: 'flex',
    alignItems: 'center',
  },
  headerActions: {
    textAlign: 'center',
  },
  list: {
    padding: '0.5rem',
  },
  item: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr',
    gap: '1rem',
    padding: '1rem',
    borderBottom: '1px solid var(--border-color)',
    alignItems: 'center',
    transition: 'background-color 0.2s',
    borderRadius: '4px',
    margin: '0.25rem 0',
  },
  name: {
    fontWeight: '500',
    color: 'var(--text-dark)',
  },
  nameText: {
    display: 'inline-block',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  mobile: {
    color: 'var(--text-medium)',
    fontSize: '0.95rem',
  },
  village: {
    color: 'var(--text-medium)',
    fontSize: '0.95rem',
  },
  district: {
    fontSize: '0.95rem',
  },
  districtBadge: {
    display: 'inline-block',
    padding: '0.3rem 0.6rem',
    backgroundColor: 'var(--background-light)',
    color: 'var(--text-medium)',
    borderRadius: '20px',
    fontSize: '0.85rem',
  },
  acreage: {
    fontSize: '0.95rem',
  },
  acreageValue: {
    display: 'inline-block',
    padding: '0.3rem 0.6rem',
    backgroundColor: 'var(--success-light)',
    color: 'var(--success-color)',
    borderRadius: '20px',
    fontSize: '0.85rem',
    fontWeight: '500',
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
    gap: '0.5rem',
  },
  viewButton: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.4rem 0.6rem',
    backgroundColor: 'var(--info-color)',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontSize: '0.85rem',
  },
  editButton: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.4rem 0.6rem',
    backgroundColor: 'var(--primary-light)',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontSize: '0.85rem',
  },
  deleteButton: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.4rem 0.6rem',
    backgroundColor: 'var(--danger-color)',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontSize: '0.85rem',
  },
  buttonIcon: {
    marginRight: '0.3rem',
    fontSize: '0.9rem',
  },
  buttonText: {
    fontWeight: '500',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem 1rem',
    color: 'var(--text-light)',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem 1rem',
    color: 'var(--danger-color)',
  },
  errorIcon: {
    fontSize: '3rem',
    marginBottom: '1rem',
  },
  errorText: {
    marginBottom: '1rem',
    textAlign: 'center',
  },
  retryButton: {
    padding: '0.5rem 1rem',
    backgroundColor: 'var(--primary-color)',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  noResults: {
    padding: '3rem 1rem',
    textAlign: 'center',
    color: 'var(--text-light)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  noResultsIcon: {
    fontSize: '3rem',
    marginBottom: '1rem',
    opacity: '0.5',
  },
  noResultsText: {
    marginBottom: '1rem',
  },
  resetButton: {
    padding: '0.5rem 1rem',
    backgroundColor: 'var(--primary-color)',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: '500',
  },
};

export default Farmer;


